import React, { useEffect } from 'react';
import { toast } from 'sonner';
import {
  generateWhatsAppShareUrl,
  generateFacebookShareUrl,
  copyToClipboard,
} from '../../utils/shareUtils';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { WhatsAppIcon, FacebookIcon, LinkIcon, CloseIcon } from './icons';
import {
  SHARE_MODAL_COLORS,
  SHARE_MODAL_SPACING,
  SHARE_MODAL_RADIUS,
  SHARE_MODAL_TYPOGRAPHY,
  SHARE_MODAL_DIMENSIONS,
} from './shareModalConstants';

export interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  activityId: number | string;
  activityTitle: string;
  shareUrl: string;
}

export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  activityId,
  activityTitle,
  shareUrl,
}) => {
  const { track } = useAnalytics();
  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Don't render if not open
  if (!isOpen) return null;

  const handleWhatsAppShare = () => {
    const whatsappUrl = generateWhatsAppShareUrl(shareUrl, activityTitle);
    window.open(whatsappUrl, '_blank');
    track('activity_shared', { share_platform: 'whatsapp', activity_id: String(activityId) });
    toast.success('Opening WhatsApp...');
    onClose();
  };

  const handleFacebookShare = () => {
    const facebookUrl = generateFacebookShareUrl(shareUrl);
    window.open(facebookUrl, '_blank', 'width=600,height=400');
    track('activity_shared', { share_platform: 'facebook', activity_id: String(activityId) });
    toast.success('Opening Facebook...');
    onClose();
  };

  const handleCopyLink = async () => {
    const success = await copyToClipboard(shareUrl);
    if (success) {
      track('activity_shared', { share_platform: 'copy', activity_id: String(activityId) });
      toast.success('Link copied to clipboard!');
      onClose();
    } else {
      toast.error('Failed to copy link');
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if clicking the backdrop, not the modal content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: SHARE_MODAL_COLORS.backdrop,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px',
      }}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-modal-title"
    >
      <div
        style={{
          backgroundColor: SHARE_MODAL_COLORS.white,
          borderRadius: SHARE_MODAL_RADIUS.modal,
          maxWidth: SHARE_MODAL_DIMENSIONS.maxWidth,
          width: '100%',
          padding: SHARE_MODAL_SPACING.modalPadding,
          position: 'relative',
          fontFamily: 'DM Sans, sans-serif',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          aria-label="Close share modal"
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            width: SHARE_MODAL_DIMENSIONS.closeButtonSize,
            height: SHARE_MODAL_DIMENSIONS.closeButtonSize,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = SHARE_MODAL_COLORS.lighterBlue;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <CloseIcon size={24} color={SHARE_MODAL_COLORS.gray6} />
        </button>

        {/* Header */}
        <div style={{ marginBottom: SHARE_MODAL_SPACING.headerMarginBottom }}>
          <h2
            id="share-modal-title"
            style={{
              fontSize: SHARE_MODAL_TYPOGRAPHY.title.size,
              fontWeight: SHARE_MODAL_TYPOGRAPHY.title.weight,
              lineHeight: SHARE_MODAL_TYPOGRAPHY.title.lineHeight,
              color: SHARE_MODAL_COLORS.gray6,
              margin: 0,
              marginBottom: 8,
            }}
          >
            Share Activity
          </h2>
          <p
            style={{
              fontSize: SHARE_MODAL_TYPOGRAPHY.subtitle.size,
              fontWeight: SHARE_MODAL_TYPOGRAPHY.subtitle.weight,
              lineHeight: SHARE_MODAL_TYPOGRAPHY.subtitle.lineHeight,
              color: SHARE_MODAL_COLORS.gray4,
              margin: 0,
            }}
          >
            {activityTitle}
          </p>
        </div>

        {/* Share Buttons */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: SHARE_MODAL_SPACING.buttonGap,
          }}
        >
          {/* WhatsApp Button */}
          <button
            onClick={handleWhatsAppShare}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 16,
              height: SHARE_MODAL_DIMENSIONS.buttonHeight,
              padding: '0 20px',
              backgroundColor: SHARE_MODAL_COLORS.white,
              border: `2px solid ${SHARE_MODAL_COLORS.lighterBlue}`,
              borderRadius: SHARE_MODAL_RADIUS.button,
              cursor: 'pointer',
              fontSize: SHARE_MODAL_TYPOGRAPHY.buttonLabel.size,
              fontWeight: SHARE_MODAL_TYPOGRAPHY.buttonLabel.weight,
              lineHeight: SHARE_MODAL_TYPOGRAPHY.buttonLabel.lineHeight,
              color: SHARE_MODAL_COLORS.gray6,
              transition: 'all 0.2s',
              fontFamily: 'DM Sans, sans-serif',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 211, 102, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <WhatsAppIcon size={24} />
            <span>Share on WhatsApp</span>
          </button>

          {/* Facebook Button */}
          <button
            onClick={handleFacebookShare}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 16,
              height: SHARE_MODAL_DIMENSIONS.buttonHeight,
              padding: '0 20px',
              backgroundColor: SHARE_MODAL_COLORS.white,
              border: `2px solid ${SHARE_MODAL_COLORS.lighterBlue}`,
              borderRadius: SHARE_MODAL_RADIUS.button,
              cursor: 'pointer',
              fontSize: SHARE_MODAL_TYPOGRAPHY.buttonLabel.size,
              fontWeight: SHARE_MODAL_TYPOGRAPHY.buttonLabel.weight,
              lineHeight: SHARE_MODAL_TYPOGRAPHY.buttonLabel.lineHeight,
              color: SHARE_MODAL_COLORS.gray6,
              transition: 'all 0.2s',
              fontFamily: 'DM Sans, sans-serif',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 119, 242, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <FacebookIcon size={24} />
            <span>Share on Facebook</span>
          </button>

          {/* Copy Link Button */}
          <button
            onClick={handleCopyLink}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 16,
              height: SHARE_MODAL_DIMENSIONS.buttonHeight,
              padding: '0 20px',
              backgroundColor: SHARE_MODAL_COLORS.white,
              border: `2px solid ${SHARE_MODAL_COLORS.lighterBlue}`,
              borderRadius: SHARE_MODAL_RADIUS.button,
              cursor: 'pointer',
              fontSize: SHARE_MODAL_TYPOGRAPHY.buttonLabel.size,
              fontWeight: SHARE_MODAL_TYPOGRAPHY.buttonLabel.weight,
              lineHeight: SHARE_MODAL_TYPOGRAPHY.buttonLabel.lineHeight,
              color: SHARE_MODAL_COLORS.gray6,
              transition: 'all 0.2s',
              fontFamily: 'DM Sans, sans-serif',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 4px 12px ${SHARE_MODAL_COLORS.lighterBlue}`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <LinkIcon size={24} color={SHARE_MODAL_COLORS.gray6} />
            <span>Copy Link</span>
          </button>
        </div>
      </div>
    </div>
  );
};
